import { useQuery, type QueryClient } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { OnboardingStepKey } from "../store/useOnboardingStore";

/** Admin's combined review outcome for the PAN + business-registration
 *  document pair — a single status covers BOTH documents, never one per
 *  document. Defaults to UNVERIFIED even before anything is uploaded.
 *
 *  `NOT_REQUIRED` is response-only on the backend (never persisted) and
 *  means this store's country has no PAN/business-registration requirement
 *  at all — returned instead of UNVERIFIED for every country outside the
 *  backend's service-type-document-requirements.config.ts (Nepal only
 *  today). See entities/store's `isServiceTypeDocumentApplicable`. */
export type ServiceTypeDocumentVerificationStatus =
  | "UNVERIFIED"
  | "VERIFIED"
  | "REJECTED"
  | "NOT_REQUIRED";

export interface ServiceTypeDocumentVerification {
  status: ServiceTypeDocumentVerificationStatus;
  // Backend stores this as a flexible JSON map — WHICH keys appear depends
  // on the store's country (Nepal today: panDocumentLegible,
  // panInformationMatches, registrationDocumentLegible,
  // registrationInformationMatches, documentsAppearLegitimate,
  // requiredDocumentsPresent; other countries may have none, or a different
  // set later). Never a fixed shape — do not destructure specific keys.
  checklist?: Record<string, boolean>;
  reviewedByUserId?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
}

/** The pair THIS store's current submission superseded, if any — the single
 *  most recent archived entry (the backend keeps the full history for the
 *  admin drawer, but the vendor only ever needs the latest one). `null`
 *  when nothing has ever been superseded (first-ever submission, or none
 *  yet). Lets the vendor still see what was rejected (files + reason)
 *  after they've already fixed and resubmitted, once the live
 *  `serviceTypeDocumentVerification` above has reset to UNVERIFIED. */
export interface ServiceTypeDocumentPreviousSubmission {
  documents: {
    panDocument: string | null;
    businessRegistrationDocument: string | null;
  };
  status: "UNVERIFIED" | "VERIFIED" | "REJECTED";
  rejectionReason: string | null;
  submittedAt: string | null;
  supersededAt: string;
}

export interface BootstrapStore {
  id: string;
  name: string | null;
  logo: string | null;
  url: string | null;
  customDomain: string | null;
}

export interface BootstrapDraftJson {
  serviceType?: {
    serviceType?: "INDIVIDUAL" | "BUSINESS";
    fullName?: string;
    citizenshipNumber?: string;
    dateOfBirth?: string;
    gender?: "MALE" | "FEMALE" | "OTHER";
    address?: string;
    companyName?: string;
    panNumber?: string;
    registrationNumber?: string;
    registeredAddress?: string;
    incorporatedDate?: string;
    // Shared phone slot used by both the INDIVIDUAL and BUSINESS branches.
    phone?: string;
    email?: string;
  };
  // Document uploads — a sibling of `serviceType`, saved via its own
  // SERVICE_TYPE_DOCUMENT step and never merged into `serviceType`.
  serviceTypeDocument?: {
    panDocument?: string;
    businessRegistrationDocument?: string;
  };
  whatYouSell?: {
    categoryIds?: string[];
  };
  templateSelection?: {
    displayName?: string;
    templateVersionId?: string;
    activeStoreTemplateId?: string;
  };
}

export interface BootstrapOnboarding {
  id: string;
  storeId: string;
  businessTypeId: string | null;
  currentStepKey: OnboardingStepKey;
  status: "IN_PROGRESS" | "COMPLETED";
  planStatus: "NONE" | "TRIAL" | "ACTIVE" | "EXPIRED";
  planCode: string | null;
  trialEndsAt: string | null;
  // Backend-derived category bucket (e.g. "ALL", "CLOTHING", "FOOTWEAR",
  // "FOOD_BEVERAGE"). Authoritative when present — drives category-aware
  // imagery / defaults in the storefront preview. Falls back to the
  // client-side `deriveCategoryPreset` over `whatYouSell.categoryIds` for
  // older onboarding records that pre-date this field.
  categoryPreset?: string | null;
  // Id of the StoreTemplate row created when the vendor adopted a template.
  // Used by the preview step to fetch the server-stitched ThemeConfig from
  // `POST /onboarding-v2/preview`.
  activeStoreTemplateId?: string | null;
  selectedTemplateVersionId?: string | null;
  suggestedTemplateVersionId?: string | null;
  version: number;
  draftJson: BootstrapDraftJson | null;
  // Sibling of draftJson, NOT nested inside draftJson.serviceTypeDocument —
  // the merchant's own document-save step replaces that JSON block
  // wholesale, so admin-set review state can't safely live inside it.
  serviceTypeDocumentVerification?: ServiceTypeDocumentVerification;
  serviceTypeDocumentPreviousSubmission?: ServiceTypeDocumentPreviousSubmission | null;
  createdAt: string;
  updatedAt: string;
}

export interface BootstrapProgression {
  requiredStepKeys: OnboardingStepKey[];
  completedStepKeys: OnboardingStepKey[];
  nextStepKey: OnboardingStepKey;
  canEnterDashboard: boolean;
  canPublish: boolean;
  nextRoute: "onboarding" | "dashboard";
}

export interface BootstrapResponse {
  store: BootstrapStore;
  onboarding: BootstrapOnboarding;
  progression: BootstrapProgression;
}

// Fetcher returns res.data, which for this endpoint is { data: { store, onboarding, progression } }
export interface BootstrapEnvelope {
  data: BootstrapResponse;
}

export const ONBOARDING_BOOTSTRAP_KEY = ["onboarding-bootstrap"] as const;

/**
 * Re-read the onboarding record outside React, with the answer in hand.
 *
 * `fetchQuery`, not `invalidateQueries`: every caller needs to *decide*
 * something from the fresh record (which step is next, whether the version
 * moved), and it refreshes the shared cache on the way through.
 */
export function fetchOnboardingBootstrap(queryClient: QueryClient) {
  return queryClient.fetchQuery<BootstrapEnvelope>({
    queryKey: ONBOARDING_BOOTSTRAP_KEY,
    queryFn: () => fetcher("/onboarding-v2/bootstrap"),
    staleTime: 0,
  });
}

export function useOnboardingBootstrap(options?: { enabled?: boolean }) {
  return useQuery<BootstrapEnvelope>({
    queryKey: ONBOARDING_BOOTSTRAP_KEY,
    queryFn: () => fetcher("/onboarding-v2/bootstrap"),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    retry: false,
  });
}
